import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faFilm,
  faNewspaper,
  faInfoCircle,
  faBars,
  faTimes,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import logo from '../assets/logo.png';
import './cssComponents/header.css';

const HeaderComponent = () => {
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const isCatalogSearchExperience = (
    location.pathname === '/watchfolioWeb'
    && new URLSearchParams(location.search).get('search') === 'true'
  );

  const menuItems = [
    { path: '/', label: 'Início', icon: faHome },
    { path: '/watchfolioWeb', label: 'Catálogo', icon: faFilm },
    { path: '/blog', label: 'Blog', icon: faNewspaper },
    { path: '/sobre', label: 'Sobre', icon: faInfoCircle },
  ];

  const closeMenu = () => setShowDropdown(false);

  useEffect(() => {
    const updateHeaderState = () => {
      setIsScrolled(window.scrollY > 24);
    };

    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    return () => window.removeEventListener('scroll', updateHeaderState);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (shellRef.current && !shellRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [showDropdown]);

  if (isCatalogSearchExperience) {
    return null;
  }

  return (
    <>
      {showDropdown && (
        <div
          className="watchfolio-header__backdrop"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      <header className={`watchfolio-header ${isScrolled ? 'watchfolio-header--scrolled' : ''}`}>
        <div className="watchfolio-header__shell" ref={shellRef}>
          <div className="watchfolio-header__brand">
            <Link to="/" aria-label="Watchfolio — página inicial" onClick={closeMenu}>
              <img src={logo} alt="Watchfolio" />
            </Link>
          </div>

          <nav
            className={`watchfolio-header__navigation ${showDropdown ? 'watchfolio-header__navigation--open' : ''}`}
            aria-label="Menu principal"
          >
            <ul role="list">
              {menuItems.map((item) => (
                <li
                  key={item.path}
                  className={location.pathname === item.path ? 'watchfolio-header__nav-item--active' : ''}
                >
                  <Link
                    to={item.path}
                    aria-label={`Navegar para ${item.label}`}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                    onClick={closeMenu}
                  >
                    <FontAwesomeIcon icon={item.icon} aria-hidden="true" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="watchfolio-header__cta">
              <Link to="/downloadApp" onClick={closeMenu}>
                <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                Baixar App
              </Link>
            </div>
          </nav>

          <button
            className={`watchfolio-header__menu-toggle ${showDropdown ? 'watchfolio-header__menu-toggle--open' : ''}`}
            onClick={() => setShowDropdown((prev) => !prev)}
            aria-label={showDropdown ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={showDropdown}
            aria-controls="mobile-nav"
          >
            <FontAwesomeIcon icon={showDropdown ? faTimes : faBars} aria-hidden="true" />
          </button>
        </div>
      </header>
    </>
  );
};

export default HeaderComponent;
